import './GalleryCoverArt.css';

/**
 * The colored "cover art" tile for a gallery card. Rendered entirely
 * in CSS — no kid-uploaded sample bytes — so a visitor can scan the
 * grid and guess what each project does without us redistributing
 * private training data.
 *
 * Three signals make each tile unique:
 *  - A gradient seeded from a hash of the project name (so the same
 *    project always looks the same, but visually different from its
 *    neighbours).
 *  - The kind glyph (📷 image, 🎙 audio, 🤸 pose, 🚀 published app)
 *    so the modality is legible at a glance.
 *  - The class labels rendered as chips. This is the *meaning* of
 *    the model, communicated up-front.
 */

export type CoverKind = 'image' | 'audio' | 'pose' | 'app';

const KIND_GLYPH: Record<CoverKind, string> = {
  image: '📷',
  audio: '🎙',
  pose: '🤸',
  app: '🚀',
};

const KIND_LABEL: Record<CoverKind, string> = {
  image: 'Camera',
  audio: 'Mic',
  pose: 'Pose',
  app: 'Live app',
};

// 8 hand-tuned palette pairs (start, end) chosen to render readably
// against light + dark themes. The seed lookup picks one
// deterministically so a given project name always looks the same.
const PALETTES: { from: string; to: string; ink: string }[] = [
  { from: '#fef3c7', to: '#fcd34d', ink: '#78350f' },
  { from: '#dbeafe', to: '#93c5fd', ink: '#1e3a8a' },
  { from: '#dcfce7', to: '#86efac', ink: '#14532d' },
  { from: '#fce7f3', to: '#f9a8d4', ink: '#831843' },
  { from: '#ede9fe', to: '#c4b5fd', ink: '#3b0764' },
  { from: '#cffafe', to: '#67e8f9', ink: '#0e7490' },
  { from: '#ffe4e6', to: '#fda4af', ink: '#881337' },
  { from: '#fef9c3', to: '#fde68a', ink: '#713f12' },
];

/** Pick a deterministic palette index from a string. djb2-ish hash. */
function paletteIndex(seed: string): number {
  let hash = 5381;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) + hash + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % PALETTES.length;
}

export interface GalleryCoverArtProps {
  name: string;
  kind: CoverKind;
  labels: string[];
}

export function GalleryCoverArt({ name, kind, labels }: GalleryCoverArtProps): JSX.Element {
  const palette = PALETTES[paletteIndex(name)] ?? PALETTES[0]!;
  const visible = labels.slice(0, 3);
  const overflow = labels.length - visible.length;
  return (
    <div
      className="bl-cover"
      role="img"
      aria-label={
        labels.length > 0
          ? `${KIND_LABEL[kind]} model — classes: ${labels.join(', ')}`
          : `${KIND_LABEL[kind]} cover art`
      }
      style={
        {
          '--cover-from': palette.from,
          '--cover-to': palette.to,
          '--cover-ink': palette.ink,
        } as React.CSSProperties
      }
    >
      <span className="bl-cover__glyph" aria-hidden="true">
        {KIND_GLYPH[kind]}
      </span>
      <span className="bl-cover__kind">{KIND_LABEL[kind]}</span>
      {visible.length > 0 && (
        <ul className="bl-cover__chips" aria-hidden="true">
          {visible.map((label) => (
            <li key={label} className="bl-cover__chip">
              {label}
            </li>
          ))}
          {overflow > 0 && <li className="bl-cover__chip bl-cover__chip--more">+{overflow}</li>}
        </ul>
      )}
    </div>
  );
}
